import { useTranslation } from "react-i18next";
import type { GetAvtaleSelskapResponse } from "@/api/generated";
import { DescriptionList, DescriptionTerm } from "@/components";
import {
  formatDateStringInterval,
  formatOrgNr,
  formatPercentage,
} from "@/utils";
import { BekreftelseDetaljerContainer } from "./BekreftelseDetaljerContainer";
import { BekreftelseSection } from "./BekreftelseSection";
import { BekreftelseTidspunkt } from "./BekreftelseTidspunkt";

export const BekreftelseAvtaleDetaljerSelskap = ({
  selskapsAvtale,
}: {
  selskapsAvtale: GetAvtaleSelskapResponse | undefined;
}) => {
  const { t } = useTranslation();

  return (
    <BekreftelseDetaljerContainer>
      <BekreftelseTidspunkt
        opprettet={selskapsAvtale?.avtale.createdAt}
        endret={selskapsAvtale?.avtale.lastUpdatedAt}
        avbestilt={selskapsAvtale?.avtale.cancelledAt}
      />
      <BekreftelseSection
        title={t("pages.bekreftelse.avtale.selskap.leverandør.tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.leverandør.navn"
            value={selskapsAvtale?.avtale.leverandør.name}
          />
        </DescriptionList>
      </BekreftelseSection>
      <BekreftelseSection
        title={t("pages.bekreftelse.avtale.selskap.aktør.tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.aktør.navn"
            value={selskapsAvtale?.avtale.avtaleEier.navn}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.aktør.organisasjonsnummer"
            value={formatOrgNr(
              selskapsAvtale?.avtale.avtaleEier.organisasjonsnummer,
            )}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.aktør.innsender"
            value={selskapsAvtale?.avtale.bestiller.navn}
          />
        </DescriptionList>
      </BekreftelseSection>
      <BekreftelseSection
        title={t("pages.bekreftelse.avtale.selskap.avtaleperiode.tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.avtaleperiode.dato"
            value={`${formatDateStringInterval(selskapsAvtale?.avtale.activeFromInclusive, selskapsAvtale?.avtale.activeToExclusive)}`}
          />
        </DescriptionList>
      </BekreftelseSection>
      <BekreftelseSection
        title={t(
          "pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.tittel",
        )}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.type-bolig"
            value={t(
              "pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.type-bolig-boligselskap",
            )}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.type-organisasjon"
            value={t("enums.boligType", {
              context: selskapsAvtale?.avtale.avtaleDetaljerSelskap?.boligType,
            })}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.kundenummer"
            value={selskapsAvtale?.avtale.kundenummer}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.måler-id"
            value={selskapsAvtale?.avtale.målerId}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.antall-boenheter"
            value={
              selskapsAvtale?.avtale.avtaleDetaljerSelskap?.antallBoenheter
            }
          />
          {selskapsAvtale?.avtale.avtaleDetaljerSelskap?.næringsarealProsent ? (
            <DescriptionTerm
              tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.andel-næringsareal"
              value={formatPercentage(
                selskapsAvtale?.avtale.avtaleDetaljerSelskap
                  ?.næringsarealProsent,
              )}
            />
          ) : (
            <DescriptionTerm
              tranlsationFeltKey="pages.bekreftelse.avtale.selskap.informasjon-om-boligselskap.næringsdrivende"
              value={t("enums.jaNei_nei")}
            />
          )}
        </DescriptionList>
      </BekreftelseSection>
    </BekreftelseDetaljerContainer>
  );
};
